// Maximal evenness: one formula — floor(i·n/k) — that yields the diatonic
// scale, the pentatonic, the °7, the octatonic, AND the world's great dance
// rhythms (Toussaint's Euclidean rhythms), depending only on k and n. The same
// arithmetic draws pixel lines (Bresenham) and places leap years.

import { useMemo, useState } from 'react';
import { Play } from 'lucide-react';
import { gapPattern, maximallyEven } from '@/music/evenness';
import { spellNote } from '@/music/notes';
import { getChordPlayer } from '@/audio/player';

interface Preset {
  label: string;
  k: number;
  n: number;
  rotation: number;
  kind: 'scale' | 'rhythm';
}

const PRESETS: Preset[] = [
  { label: 'Major scale · ME(7,12)', k: 7, n: 12, rotation: 11, kind: 'scale' },
  { label: 'Pentatonic · ME(5,12)', k: 5, n: 12, rotation: 0, kind: 'scale' },
  { label: '°7 · ME(4,12)', k: 4, n: 12, rotation: 0, kind: 'scale' },
  { label: 'Whole-tone · ME(6,12)', k: 6, n: 12, rotation: 0, kind: 'scale' },
  { label: 'Octatonic · ME(8,12)', k: 8, n: 12, rotation: 0, kind: 'scale' },
  { label: 'Tresillo · ME(3,8)', k: 3, n: 8, rotation: 0, kind: 'rhythm' },
  { label: 'Cinquillo · ME(5,8)', k: 5, n: 8, rotation: 0, kind: 'rhythm' },
  { label: 'Bossa rhythm · ME(5,16)', k: 5, n: 16, rotation: 0, kind: 'rhythm' },
];

const SIZE = 280;
const C = SIZE / 2;
const R = C - 30;

export function EvennessExhibit() {
  const [n, setN] = useState(12);
  const [k, setK] = useState(7);
  const [rotation, setRotation] = useState(11);

  const onsets = useMemo(() => maximallyEven(k, n, rotation), [k, n, rotation]);
  const onsetSet = useMemo(() => new Set(onsets), [onsets]);
  const gaps = useMemo(() => gapPattern(onsets, n), [onsets, n]);

  const angle = (i: number) => ((i / n) * 2 - 0.5) * Math.PI;
  const x = (i: number, r: number) => C + r * Math.cos(angle(i));
  const y = (i: number, r: number) => C + r * Math.sin(angle(i));

  const playScale = () => {
    void getChordPlayer().playMidi(
      onsets.map((p) => 60 + p),
      0.22,
    );
  };
  const playRhythm = () => {
    const stepMs = 132; // a brisk sixteenth grid
    onsets.forEach((p) => {
      setTimeout(() => void getChordPlayer().playNote(p === onsets[0] ? 69 : 76, 0.14), p * stepMs);
    });
  };

  const applyPreset = (p: Preset) => {
    setN(p.n);
    setK(p.k);
    setRotation(p.rotation);
    setTimeout(() => (p.kind === 'scale' ? playScale : playRhythm)(), 0);
  };

  return (
    <div className="lab-exhibit" data-testid="lab-evenness">
      <p className="lab-lede">
        Spreading <strong>k</strong> notes over <strong>n</strong> slots as evenly as whole numbers
        allow (<code>floor(i·n/k)</code>) gives the diatonic scale (7 of 12), the pentatonic (5 of
        12), the °7 (4 of 12) and the octatonic (8 of 12). Read as time instead of pitch, it gives
        the tresillo, the cinquillo and the bossa rhythm. The same rule draws lines of pixels
        (Bresenham&rsquo;s algorithm) and places leap years.
      </p>

      <div className="lab-controls" style={{ flexWrap: 'wrap' }}>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            className="btn btn-sm"
            onClick={() => applyPreset(p)}
            data-testid={`me-preset-${p.k}-${p.n}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="lab-row">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          style={{ width: '100%', maxWidth: 300 }}
          role="img"
          aria-label={`Maximally even ${k} of ${n}`}
        >
          <circle cx={C} cy={C} r={R} fill="none" stroke="var(--hairline)" />
          {onsets.length > 1 && (
            <polygon
              points={onsets.map((p) => `${x(p, R)},${y(p, R)}`).join(' ')}
              fill="var(--accent)"
              fillOpacity={0.13}
              stroke="var(--accent)"
              strokeWidth={1.8}
              strokeLinejoin="round"
            />
          )}
          {Array.from({ length: n }, (_, i) => (
            <g key={i}>
              <circle
                cx={x(i, R)}
                cy={y(i, R)}
                r={onsetSet.has(i) ? 6 : 2.6}
                fill={onsetSet.has(i) ? 'var(--accent)' : 'var(--faint)'}
              />
              <text
                x={x(i, R + 15)}
                y={y(i, R + 15)}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={9.5}
                fill={onsetSet.has(i) ? 'var(--ink)' : 'var(--muted)'}
                fontWeight={onsetSet.has(i) ? 700 : 400}
              >
                {n === 12 ? spellNote(i, 'flat') : i}
              </text>
            </g>
          ))}
          <text
            x={C}
            y={C}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={14}
            fontWeight={700}
            fill="var(--ink)"
            data-testid="me-readout"
          >
            {k} of {n}
          </text>
        </svg>

        <div className="lab-panel">
          <div className="lab-controls" style={{ flexWrap: 'wrap' }}>
            <label className="control-group">
              <span className="label">Slots n = {n}</span>
              <input
                type="range"
                min={3}
                max={16}
                value={n}
                onChange={(e) => {
                  const nn = Number(e.target.value);
                  setN(nn);
                  setK(Math.min(k, nn));
                  setRotation(0);
                }}
                aria-label="Slots"
              />
            </label>
            <label className="control-group">
              <span className="label">Onsets k = {k}</span>
              <input
                type="range"
                min={1}
                max={n}
                value={k}
                onChange={(e) => {
                  setK(Number(e.target.value));
                  setRotation(0);
                }}
                aria-label="Onsets"
              />
            </label>
            <label className="control-group">
              <span className="label">Rotate = {rotation}</span>
              <input
                type="range"
                min={0}
                max={n - 1}
                value={rotation}
                onChange={(e) => setRotation(Number(e.target.value))}
                aria-label="Rotation"
              />
            </label>
          </div>
          <p
            data-testid="me-pattern"
            style={{ fontFamily: 'monospace', fontSize: 15, letterSpacing: 2 }}
          >
            {Array.from({ length: n }, (_, i) => (onsetSet.has(i) ? '×' : '·')).join('')}
          </p>
          <p className="muted">
            Step pattern: {gaps.join('–')}. A maximally even set has at most two step sizes, one
            apart: 2 and 1 in the major scale, 3 and 2 in the tresillo.
          </p>
          <div className="lab-controls">
            {n === 12 && (
              <button className="btn btn-sm" onClick={playScale} data-testid="me-play-scale">
                <Play size={14} fill="currentColor" aria-hidden="true" />
                As a scale
              </button>
            )}
            <button className="btn btn-sm" onClick={playRhythm} data-testid="me-play-rhythm">
              <Play size={14} fill="currentColor" aria-hidden="true" />
              As a rhythm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
